import type { StructuredDirectorError } from './errors.js';
import { formatDirectorErrorForUi } from './errors.js';
import type { OutputSchemaId } from './validate.js';
import { validateDirectorOutputById } from './validate.js';
import type { PromptPurpose, PromptRequest, PromptSourceBundle } from './types.js';
import {
  compileAssumption,
  compileCharacterOptions,
  compileConnectedQuestions,
  compileLocationSummary,
  compileMissedActivity,
  compileRuntimeScene,
  compileWorldDevices,
} from './compilers/index.js';

export interface CompileOptions {
  unansweredDependencyIds?: string[];
  fieldPath?: string;
  missingFieldLabel?: string;
  draftActorId?: string;
  persistedEventIds?: string[];
  priorSummaryHash?: string;
}

export function compilePromptRequest(
  purpose: PromptPurpose,
  source: PromptSourceBundle,
  options: CompileOptions = {},
): PromptRequest {
  switch (purpose) {
    case 'connected_questions':
      return compileConnectedQuestions(source, options.unansweredDependencyIds ?? []);
    case 'assumption':
      return compileAssumption(
        source,
        options.fieldPath ?? 'unknown',
        options.missingFieldLabel ?? options.fieldPath ?? 'unknown',
      );
    case 'world_devices':
      return compileWorldDevices(source);
    case 'character_options':
      return compileCharacterOptions(source, options.draftActorId ?? '');
    case 'runtime_scene':
      return compileRuntimeScene(source);
    case 'location_summary':
      return compileLocationSummary(source, options.persistedEventIds ?? []);
    case 'missed_activity':
      return compileMissedActivity(
        source,
        options.persistedEventIds ?? [],
        options.priorSummaryHash,
      );
    default: {
      const _exhaustive: never = purpose;
      throw new Error(`Unknown prompt purpose: ${String(_exhaustive)}`);
    }
  }
}

/** Deterministic resolution when provider unavailable, validation fails, or budget overflow. */
export function resolveWithFallback<T>(
  request: PromptRequest,
  validationOk: boolean,
  hasApiKey: boolean,
): { data: T; manifest: PromptRequest['provenance'] } {
  const useLocal = !hasApiKey || !validationOk;
  const data = useLocal ? (request.fallback.local() as T) : (null as unknown as T);
  const manifest = {
    ...request.provenance,
    provider: useLocal ? ('local' as const) : request.provenance.provider,
    fallbackUsed: useLocal,
    validationStatus: useLocal
      ? ('fallback' as const)
      : validationOk
        ? ('valid' as const)
        : ('invalid' as const),
  };
  return { data, manifest };
}

/** Validate provider output and map failures to recoverable structured errors. */
export function resolveValidatedOutput<T = unknown>(
  request: PromptRequest,
  raw: unknown,
):
  | { ok: true; data: T; manifest: PromptRequest['provenance'] }
  | { ok: false; error: StructuredDirectorError; uiMessage: string; manifest: PromptRequest['provenance'] } {
  const schemaId = request.outputSchemaId as OutputSchemaId;
  const validated = validateDirectorOutputById(schemaId, raw);
  if (validated.ok) {
    return {
      ok: true,
      data: validated.data as T,
      manifest: {
        ...request.provenance,
        validationStatus: 'valid',
        fallbackUsed: false,
      },
    };
  }
  return {
    ok: false,
    error: validated.error,
    uiMessage: formatDirectorErrorForUi(validated.error),
    manifest: {
      ...request.provenance,
      validationStatus: 'invalid',
      fallbackUsed: false,
    },
  };
}
